import React from 'react';

import { receivingCityOptions } from '@data/cities';
import { availableServiceNames, routePlanStrings } from '@data/routePlan';
import {
  BookingInquiryAdminGroupNames,
  BookingInquiryForm,
  InquiryDetail,
  TradeCondition,
} from '@models/BookingInquiry';
import { customsInstructionFormInitialValues } from '@models/CustomsInstruction';
import { deliveryInstructionFormInitialValues } from '@models/DeliveryInstruction';
import { createStrings } from '@utils/local';
import dayjs from 'dayjs';

export interface Step {
  name: string;
  label: string;
}

export const inquiryTitleStrings = createStrings({
  ja: {
    questionInput: 'フォーム入力',
    selectRoutePlan: `${routePlanStrings.route}選択`,
    confirmation: '内容確認',
    complete: '予約完了',
  },
});

export const steps: Step[] = [
  {
    name: 'InquiryForm',
    label: inquiryTitleStrings.questionInput,
  },
  {
    name: 'SelectRoutePlan',
    label: inquiryTitleStrings.selectRoutePlan,
  },
  {
    name: 'Confirmation',
    label: inquiryTitleStrings.confirmation,
  },
  {
    name: 'Complete',
    label: inquiryTitleStrings.complete,
  },
];

export const bookingInquiryStrings = createStrings({
  ja: {
    d2dNo: 'D2D番号',
    creator: '作成者',
    shipper: 'SHIPPER（輸出者）',
    shipperSide: 'SHIPPER側',
    consignee: 'CNEE（輸入者）',
    consigneeSide: 'CNEE側',
    email: 'メール',
    companyName: '会社名',
    customerPrincipal: '客側担当者',
    memberNo: '会員番号',
    ReceiptionTime: '受付時間',
    receiver: '納品先',
    owner: 'OWNER',
    pickupAddress: '集荷先',
    pickup_address: '集荷先',
    preferredArrivalDate: '納品希望日',
    preferredArrivalDatePlaceholder: '例：4月15日までお願いします',
    hasPickupsPrompt: '中国国内の有料集荷サービスはご利用されますか？',
    needsPickup: '集荷必要',
    noPickup: '集荷不要',
    portName: '中国荷受倉庫',
    warehouse: '荷受倉庫',
    warehouseDate: '倉庫搬入日',
    computeByLWH: '荷物明細（L:W:H）計算',
    computeByWV: '総重量体積計算',
    standardWeight: '費用計算基準重量',
    cargos: '荷物の内容',
    cargoName: '商品品名',
    cargoNum: '総個数 (PKG)',
    cargoSize: 'サイズ (L:W:H cm)',
    length: '長さ',
    width: '幅',
    height: '高さ',
    weight: '重さ (KGS)',
    totalWeight: '総重量 (KGS)',
    totalVolume: '総メジャー (CBM)',
    volume: 'm³',
    tradeCondition: '貿易条件',
    collectOption: '中国国内集荷サービス',
    deliveryOption: '納品オプション',
    receivingWarehouse: '中国国内荷受倉庫',
    memo: 'メモ',
    ddp: 'DDP',
    exw: 'EXW',
    dduJapan: 'DDU 検査料日本払い',
    fca: 'FCA',
    dduChina: 'DDU 検査料中国払い',
    customerNo: 'お客様の管理番号',
    tradingAgency: '貿易代行サービス',
    useRcep: 'RCEPのご利用',
    useTradingAgency: '貿易代行サービス利用要否',
    tradingAgencyPayer: '貿易代行サービス請求先',
    specialNote: '特記事項',
    selfSpecialNote: 'その他の特記事項',
    hasSpecialNoteFalse: 'その他の特記事項なし',
    hasSpecialNoteTrue: 'その他の特記事項あり',
    specialNotePlaceholder:
      '通関と国内トラック配送以外の特記事項ありを選択した場合、こちらのご入力は必須です。',
    checkCautionsMessage: '注意事項を確認してください',
    notFound: 'この連絡先は見つかりません。もう一度試してください。',
    additionalFile: '添付資料',
    receiverMissingZipcode: '納品先の郵便番号を入力してください。',
    receiverWrongZipcode: '納品先の郵便番号を修正してください。',
    pickupAddressWrongZipcode: '集荷先の郵便番号を修正してください。',
    atLeastOneCargo: '荷物を入力してください。',
    atLeastOnePickupAddress: '集荷先を入力してください。',
    totalCargos: '出荷合計',
    invalidChinaZipcodes: (codes: string[]) =>
      `ご入力の中国郵便番号は見つかりませんでした：${codes.join('、')}`,
    adminGroupTitle: (name: BookingInquiryAdminGroupNames) => {
      return {
        shipper: bookingInquiryStrings.shipper,
        receiver: `${bookingInquiryStrings.receiver}と納品希望日`,
        consignee: bookingInquiryStrings.consignee,
        cargos: `${bookingInquiryStrings.pickupAddress}と荷物の内容`,
        miscInfo: `${bookingInquiryStrings.tradeCondition}、${bookingInquiryStrings.tradingAgency}と${bookingInquiryStrings.useRcep}`,
        specialNote: bookingInquiryStrings.specialNote,
        customerNo: bookingInquiryStrings.customerNo,
        routePlan: `ルート情報`,
      }[name];
    },
    truckTypes: {
      any: '指定なし',
      mix: '混載',
      '4T': '4t車1台',
      '10T': '10t車1台',
      '4Tx2': '4t車2台',
    },
    followerEmail: 'メール',
    pickupDate: '集荷日',
    moveInDate: '荷受倉庫への搬入予定日',
    moveInDate2: '搬入予定日',
    bestDeliveryDate: '最短納品可能日',
    count: '個',
    invalidZipcode: '有効な郵便番号を入力してください。',
    moveInDateHoliday: (
      <>
        年末年始荷受倉庫休業期間：
        <br />
        2022年12月27日（火）より2023年1月2日（月）まで
      </>
    ),
    moveInDateHolidayError: '休業期間以外の日付を選択してください。',
    receivingWarehouseInHoliday: '荷受倉庫休業中',
    maxCargos:
      '荷物は２０点までお願いします。種類が多い場合は「,」で区切って同じ欄に入力してください。',
    salespersonCustoms: '通関担当者',
    printBookingInquiryDisplay: '予約内容印刷',
    printBookingInquiryPriceSettlement: '試算金額印刷',
    errorBecomeSalespersonJapan: `当ユーザーはCS担当者（日本）になることができません。Adminにお問い合わせください。`,
    errorBecomeSalespersonChina: `当ユーザーはCS担当者（中国）になることができません。Adminにお問い合わせください。`,
    errorBecomeSalespersonCustoms: `当ユーザーは通関担当者になることができません。Adminにお問い合わせください。`,
    errorSalespersonOnlyEdit:
      '担当者のみが案件を編集・操作することができます。Adminにお問い合わせください。',
    errorSalespersonCustomsOnlyEdit:
      '通関担当者のみが案件を編集・操作することができます。Adminにお問い合わせください。',
    errorSalespersonJapanOnlyEdit:
      '担当者（日本）のみが案件を編集・操作することができます。Adminにお問い合わせください。',
    errorMissingVesselSchedule: 'PreAlertでVessel Scheduleを選択してください。',
    errorMissingEta: 'Vessel ScheduleでETAを入力してください。',
    errorMissingExchangeRate: '指定の日(ETA-2)の為替レートはありません。ITに連絡してください。',
    errorUnlockAdminOnly: 'UNLOCK操作はAdminのみです。Adminにお問い合わせください。',
    errorPreAlertIsEmpty: 'PreAlertは作成されていません。',
    errorSeaWaybillPreAlertIsEmpty: [
      'Pre-alertがまだ作成されていないため、Sea Waybillをアップロードすることができません。',
      'Pre-alert还未作成，无法上传Sea Waybill。',
    ],
  },
} as const);

export const bookingCautionsStrings = createStrings({
  ja: {
    alertBanner: '危険品は、基本的にお取り扱いできません。',
    importantCargosTitle: '特に注意して頂きたいお荷物',
    cosmetics: 'コスメ',
    battery: '電池・バッテリー',
    dangerous: '危険品',
    pharmaceutical: '医薬品',
    tableware: '食器',
    kitchenware: '調理器具',
    food: '食品',
    livingThings: '動植物',
    toys: () => (
      <>
        玩具
        <span style={{ fontSize: 8 }}>6歳以下</span>
      </>
    ),
    intellectural: 'ライセンスなし知的財産',
    intellecturalMobile: () => (
      <>
        ライセンスなし
        <br />
        知的財産
      </>
    ),
    inputNotesPrompt: () => (
      <>
        以下の条件に当てはまる場合、必ず
        <span>特記事項</span>
        欄にその旨をご記載ください。
      </>
    ),
    notes: () => [
      ['アマゾンや楽天の倉庫へのお届けを希望している。'],
      [
        '電池・バッテリーを内蔵している商品である。',
        '※商品によってはD2Dのご利用が難しい場合がございますので、必ず電池容量、 SDS(Safety Data Sheet安全データシート)あるいはMSDS(Material Safety Data Sheet 化学物質等安全データシート) などの情報をご提供してください。 ',
      ],
      ['商品の特殊な形状や上積み禁止のお荷物である。', '※別途料金をご請求することがございます。'],
      [
        'お荷物のなかに長尺もの（1辺の長さが2m以上）または重量貨物（１個の重量が1000KGS以上）がある。',
      ],
      ['温度管理を必要とする商品である。', '※扱う事ができない場合がございます。'],
      [
        '輸送時に固定、緩衝材の使用など、輸送方法に配慮・工夫が必要である。',
        '※緩衝材や固定の材料費をオプションサービスとしてご請求する事がございます。 ',
      ],
      ['破損しやすい商品である。'],
    ],
  },
});

export const bookingInquiryTradeConditionStrings = createStrings({
  ja: {
    DDP: 'DDP',
    EXW: 'EXW',
    DDU: 'DDU 検査料-日本払い',
    FCA: 'FCA',
    DDU_CHINA: 'DDU 検査料-中国払い',
  },
});

export const displayedTradeConditions: TradeCondition[] = ['EXW', 'FCA', 'DDU', 'DDU_CHINA', 'DDP'];

export const sampleFormHasPickups: BookingInquiryForm = {
  shipper: 1355,
  consignee: 1283,
  receiver: 1284,
  shipper_input: undefined,
  consignee_input: undefined,
  receiver_input: undefined,
  trade_condition: 'DDU',
  customer_no: '',
  is_caution_checked: ['Y'],
  use_trading_agency: 'USED',
  trading_agency_payer: 'cn',
  preferred_arrival_date: '',
  file: [],
  has_pickups: 'true' as any,
  cargos_port_name: '',
  cargos2: [
    {
      id: 40026,
      tag: 'WV',
      name: 'TSUKIKAGE',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
    {
      id: 3271,
      tag: 'WV',
      name: 'HANATSUKIYO',
      num: 1,
      total_volume: 3,
      total_weight: 2,
      pickup_address_index: 2,
    },
    {
      id: 62963,
      tag: 'WV',
      name: 'KETSUBETSU',
      num: 1,
      total_volume: 1,
      total_weight: 10,
      pickup_address_index: 1,
    },
    {
      id: 31686,
      tag: 'WV',
      name: 'SANGESHI',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
  ],
  cargos_pickup_addresses: [1359, 1357],
  cargos_pickup_address_inputs: [],
  pickup_date: dayjs().add(1).format('YYYY-MM-DD'),
  move_in_date: '',
  route_plan_order: 'RECOMMENDED',
  service_names: availableServiceNames,
  receiving_warehouse_cities: receivingCityOptions.map((o) => o.name),
  route_plan: -1,
  route_plan_simulator_result: undefined,
  use_rcep: 'false' as any,
  customs_instruction: customsInstructionFormInitialValues,
  delivery_instruction: deliveryInstructionFormInitialValues,
  has_special_note: true,
  special_note: 'OK',
};

export const sampleFormNoPickupsDev: BookingInquiryForm = {
  shipper: 1355,
  consignee: 1283,
  receiver: 1284,
  shipper_input: undefined,
  consignee_input: undefined,
  receiver_input: undefined,
  trade_condition: 'DDU',
  customer_no: '',
  is_caution_checked: [],
  use_trading_agency: 'USED',
  trading_agency_payer: 'cn',
  preferred_arrival_date: '',
  file: [],
  has_pickups: 'false' as any,
  cargos_port_name: 'GUANGDONG',
  cargos2: [
    // {
    //   id: 40026,
    //   tag: 'WV',
    //   name: '4432',
    //   total_volume: 1,
    //   total_weight: 1,
    //   pickup_address_index: undefined,
    // },
    {
      id: 1,
      tag: 'LWH',
      name: 'KASA',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 2,
      tag: 'LWH',
      name: 'KASA',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 3,
      tag: 'LWH',
      name: 'SHIT',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 4,
      tag: 'LWH',
      name: 'WINNIE',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 5,
      tag: 'LWH',
      name: 'LAODENG',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
  ],
  cargos_pickup_addresses: [],
  cargos_pickup_address_inputs: [],
  pickup_date: '',
  move_in_date: dayjs().add(1).format('YYYY-MM-DD'),
  route_plan_order: 'RECOMMENDED',
  service_names: availableServiceNames,
  receiving_warehouse_cities: receivingCityOptions.map((o) => o.name),
  route_plan: -1,
  route_plan_simulator_result: undefined,
  use_rcep: 'false' as any,
  customs_instruction: customsInstructionFormInitialValues,
  delivery_instruction: deliveryInstructionFormInitialValues,
  has_special_note: true,
  special_note: 'OK',
};

export const sampleFormHasPickupsHandInputDev: BookingInquiryForm = {
  shipper: 0,
  consignee: 0,
  receiver: 0,
  shipper_input: {
    company_name: '伊顿公学',
    english_name: 'Eden High School',
    zip_code: '518000',
    address1: 'Guangdong, Shenzhen',
    address1_cn: '广东, 深圳',
    address2: '25A Block 1, Boning Garden, Nanshan District',
    principal_name: '约翰 凯恩斯',
    phone: '09063971234',
    email: 'johncaines@gmail.com',
    sns_id: 'fuckcpp9641',
    social_credit_id: '',
    user: undefined,
  },
  consignee_input: {
    company_name: '株式会社凸版',
    english_name: 'Toppan Ltd. Co.',
    zip_code: '1100016',
    address1: '東京都台東区',
    address1_cn: '东京都台东区',
    address2: '台東1-15-6 114',
    principal_name: '千葉もも',
    phone: '09063971234',
    email: 'johncaines@gmail.com',
    sns_id: 'fuckcpp9641',
    social_credit_id: '',
    user: undefined,
  },
  receiver_input: {
    company_name: '株式会社凸版',
    english_name: 'Toppan Ltd. Co.',
    zip_code: '1100016',
    address1: '東京都台東区',
    address1_cn: '东京都台东区',
    address2: '台東1-15-6 114',
    principal_name: '千葉もも',
    phone: '09063971234',
    email: 'johncaines@gmail.com',
    sns_id: 'fuckcpp9641',
    social_credit_id: '',
    user: undefined,
  },
  trade_condition: 'DDU',
  customer_no: '',
  is_caution_checked: ['Y'],
  use_trading_agency: 'USED',
  trading_agency_payer: 'cn',
  preferred_arrival_date: '',
  file: [],
  has_pickups: 'true' as any,
  cargos_port_name: '',
  cargos2: [
    {
      id: 40026,
      tag: 'WV',
      name: '月影揺れる',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
    {
      id: 3271,
      tag: 'WV',
      name: '花月夜',
      num: 1,
      total_volume: 3,
      total_weight: 2,
      pickup_address_index: 2,
    },
    {
      id: 62963,
      tag: 'WV',
      name: '決別の旅',
      num: 1,
      total_volume: 1,
      total_weight: 10,
      pickup_address_index: 1,
    },
    {
      id: 31686,
      tag: 'WV',
      name: '蒼月の懺悔詩',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
  ],
  cargos_pickup_addresses: [],
  cargos_pickup_address_inputs: [
    {
      id: 1,
      contact: {
        company_name: '伊顿公学',
        english_name: 'Eden High School',
        zip_code: '518000',
        address1: 'Guangdong, Shenzhen',
        address1_cn: '广东省, 深圳',
        address2: '25A Block 1, Boning Garden, Nanshan District',
        principal_name: '约翰 凯恩斯',
        phone: '09063971234',
        email: 'johncaines@gmail.com',
        sns_id: 'fuckcpp9641',
        social_credit_id: '',
        user: undefined,
      },
    },
    {
      id: 2,
      contact: {
        company_name: '香港大学',
        english_name: 'Hong Kong University',
        zip_code: '518000',
        address1: 'Guangdong, Shenzhen',
        address1_cn: '广东省, 深圳',
        address2: '25A Block 1, Boning Garden, Nanshan District',
        principal_name: '约翰 凯恩斯',
        phone: '09063971234',
        email: 'johncaines@gmail.com',
        sns_id: 'fuckcpp9641',
        social_credit_id: '',
        user: undefined,
      },
    },
  ],
  pickup_date: dayjs().add(1).format('YYYY-MM-DD'),
  move_in_date: '',
  route_plan_order: 'RECOMMENDED',
  service_names: availableServiceNames,
  receiving_warehouse_cities: receivingCityOptions.map((o) => o.name),
  route_plan: -1,
  route_plan_simulator_result: undefined,
  use_rcep: 'false' as any,
  customs_instruction: customsInstructionFormInitialValues,
  delivery_instruction: deliveryInstructionFormInitialValues,
  has_special_note: true,
  special_note: 'OK',
};

export const sampleFormNoPickupsQa: BookingInquiryForm = {
  shipper: 809,
  consignee: 854,
  shipper_input: undefined,
  consignee_input: undefined,
  receiver_input: undefined,
  trade_condition: 'DDU',
  customer_no: '',
  is_caution_checked: [],
  use_trading_agency: 'USED',
  trading_agency_payer: 'cn',
  preferred_arrival_date: '',
  file: [],
  has_pickups: 'false' as any,
  cargos_port_name: 'SHANGHAI',
  cargos2: [
    {
      id: 1,
      tag: 'LWH',
      name: 'KASA',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 2,
      tag: 'LWH',
      name: 'KASA',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 3,
      tag: 'LWH',
      name: 'SHIT',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 4,
      tag: 'LWH',
      name: 'WINNIE',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
    {
      id: 5,
      tag: 'LWH',
      name: 'LAODENG',
      length: 30,
      width: 40,
      height: 50,
      num: 3,
      weight: 10,
      pickup_address_index: 0,
    },
  ],
  cargos_pickup_addresses: [],
  cargos_pickup_address_inputs: [],
  pickup_date: '2023-01-12',
  move_in_date: '2023-01-24',
  route_plan_order: 'RECOMMENDED',
  service_names: ['D2D', 'D2DSHENZHEN+'],
  receiving_warehouse_cities: receivingCityOptions.map((o) => o.name),
  route_plan: -1,
  use_rcep: 'false' as any,
  route_plan_simulator_result: undefined,
  receiver: 855,
  customs_instruction: customsInstructionFormInitialValues,
  delivery_instruction: deliveryInstructionFormInitialValues,
  has_special_note: true,
  special_note: 'OK',
};

export const sampleFormHasPickupsWrongIds: BookingInquiryForm = {
  shipper: 1355,
  consignee: 1283,
  receiver: 1284,
  shipper_input: undefined,
  consignee_input: undefined,
  receiver_input: undefined,
  trade_condition: 'DDU',
  customer_no: '',
  is_caution_checked: ['Y'],
  use_trading_agency: 'USED',
  trading_agency_payer: 'cn',
  preferred_arrival_date: '',
  file: [],
  has_pickups: 'true' as any,
  cargos_port_name: '',
  cargos2: [
    {
      id: 40026,
      tag: 'WV',
      name: 'TSUKIKAGE',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
    {
      id: 3271,
      tag: 'WV',
      name: 'HANATSUKIYO',
      num: 1,
      total_volume: 3,
      total_weight: 2,
      pickup_address_index: 2,
    },
    {
      id: 62963,
      tag: 'WV',
      name: 'KETSUBETSU',
      num: 1,
      total_volume: 1,
      total_weight: 10,
      pickup_address_index: 1,
    },
    {
      id: 31686,
      tag: 'WV',
      name: 'SANGESHI',
      num: 1,
      total_volume: 1,
      total_weight: 1,
      pickup_address_index: 1,
    },
  ],
  cargos_pickup_addresses: [1359, 1357, -123123],
  cargos_pickup_address_inputs: [],
  pickup_date: dayjs().add(1).format('YYYY-MM-DD'),
  move_in_date: '',
  route_plan_order: 'RECOMMENDED',
  service_names: availableServiceNames,
  receiving_warehouse_cities: receivingCityOptions.map((o) => o.name),
  route_plan: -1,
  route_plan_simulator_result: undefined,
  use_rcep: 'false' as any,
  customs_instruction: customsInstructionFormInitialValues,
  delivery_instruction: deliveryInstructionFormInitialValues,
  has_special_note: true,
  special_note: 'OK',
};

export const exampleInquiryDetail: InquiryDetail = {
  id: 910,
  d2d_no: 'Y-36119146',
  user: {
    last_name: 'Wang',
    first_name: 'Chenyu',
    email: 'wangchenyu2017@gmail.com',
  },
  salesperson: null,
  file: null,
  shipper: {
    company_name: '南方物流',
    english_name: '',
    zip_code: '900007',
    address1: '中国上海',
    address1_cn: '',
    address2: '',
    principal_name: '\\u5218\\u5fd7\\u8d85',
    phone: '18803561683',
    email: '',
    sns_id: '',
    social_credit_id: '',
    user: null,
  },
  receiver: {
    company_name: '海航集团',
    english_name: '',
    zip_code: '900007',
    address1: '2313',
    address1_cn: '',
    address2: '',
    principal_name: '12313',
    phone: '123133',
    email: '',
    sns_id: '',
    social_credit_id: '',
    user: null,
  },
  consignee: {
    company_name: '海航集团1',
    english_name: '',
    zip_code: '900007',
    address1: '2313',
    address1_cn: '',
    address2: '',
    principal_name: '12313',
    phone: '123133',
    email: '',
    sns_id: '',
    social_credit_id: '',
    user: null,
  },
  has_pickups: false,
  cargos: {
    cargos_by_port: [
      {
        port_name: '\\u4e0a\\u6d77',
        cargos: [
          { name: '1', tag: 'LWH', num: 1, length: 1.0, width: 1.0, height: 1.0, weight: 1.0 },
        ],
      },
    ],
    cargos_by_pickup_address: undefined,
  },
  customer_no: '12313',
  preferred_arrival_date: '4\\u670815\\u65e5',
  trade_condition: 'DDP',
  is_caution_checked: true,
  special_note: '12313',
  can_views: [
    {
      id: null,
      can_view_type: 'creator',
      user: 2,
      company_name: '商汤科技',
      email: 'wangchenyu2017@gmail.com',
    },
    {
      id: null,
      can_view_type: 'owner',
      user: 2,
      company_name: '商汤科技',
      email: 'wangchenyu2017@gmail.com',
    },
  ],
  is_invoice_done: false,
  is_payment_slip_done: false,
  use_trading_agency: 'USED',
  trading_agency_payer: 'cn',
  use_rcep: null,
  has_special_note: false,
  memo: '',
  status: '進行中の案件を作成',
  route_plan: null,
  pickup_date: null,
  move_in_date: '2023-02-01',
  route_plan_simulator_result: null,
  updated_at: '2023-04-05T14:56:45.485435+09:00',
  created_at: '2023-01-26T18:09:12.258723+09:00',
  customs_instruction: {
    special_note: [
      {
        note: '一々岡田さんが養成事ちょっと誤解にありない気そんな事業それか危くをについてお矛盾ですたでますと、同じ結果はみんなか道主義がやっつけて',
        title: '個別評価申告',
        selected: true,
      },
      {
        note: 'その余裕をは困るたばという種類にいいながら来るだた。',
        title: '包括評価申告',
        selected: true,
      },
      {
        note: '今がはできるだけ見て困らだたでなけれが、もうどうもなっけれども反抗もずいぶんないんのない。',
        title: '食品申請',
        selected: true,
      },
      {
        note: '',
        title: '化粧品関係書類',
        selected: false,
      },
      {
        note: '',
        title: '医療機器を扱うライセンス',
        selected: false,
      },
      {
        note: '',
        title: '知的財産許諾書',
        selected: false,
      },
      {
        note: '',
        title: 'その他',
        selected: false,
      },
    ],
    has_special_note: true,
  },
  delivery_instruction: {
    special_note: 'xxxxtasdascfas',
    has_special_note: true,
  },
};
